import { RefObject, useRef } from "react";

import { Button, Intent } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { useHead } from "@unhead/react";

import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { Box, Flex } from "@/components/layout/flexbox";
import { PublicFooter } from "@/components/nav/PublicFooter";
import { PublicNav } from "@/components/nav/PublicNav";
import { Container } from "@/components/pieces/bootstrap";
import { DOMAIN, SITE_NAME, STANDARD_NAV_HEIGHT } from "@/css/constants";

import { DocumentContent } from "./DocumentContent";
import { generateDocx } from "./export-doc";

interface ExportButtonProps {
  contentRef: RefObject<HTMLDivElement | null>;
  filename?: string;
  title?: string;
}

function ExportButton({
  contentRef,
  filename = "document.docx",
  title,
}: ExportButtonProps) {
  const handleExport = async (): Promise<void> => {
    if (!contentRef?.current) return;

    try {
      const blob = await generateDocx(contentRef.current, title);
      if (!blob) return;

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  return (
    <div>
      <Button
        onClick={handleExport}
        icon="download"
        intent={Intent.PRIMARY}
        minimal
        small
      >
        Export as Word Document
      </Button>
    </div>
  );
}

const StyledContainer = styled(Container)`
  margin-bottom: 100px;
  width: 800px;
  line-height: 150%;

  .indent {
    padding-left: 24px;
  }

  h2 {
    margin-top: 32px;
    margin-bottom: 24px;
    span {
      color: #003881;
      margin-right: 4px;
    }
  }

  h4 {
    margin-top: 24px;
    margin-bottom: 12px;
    font-weight: 600;
    font-size: 22px;

    span {
      color: #003881;
      margin-right: 8px;
    }
  }

  ol {
    margin-top: 16px;
    margin-bottom: 16px;

    li {
      margin-bottom: 6px;
    }
  }
`;

export function EnterpriseTosPage() {
  useHead({
    bodyAttrs: { class: "public-site" },
  });

  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <SEOHead
        title={`Enterprise Terms of Service at ${SITE_NAME}`}
        description={`Learn more about ${SITE_NAME}'s Terms of Service, which governs your use of ${SITE_NAME} and the products, features, apps, services, technologies, and software we offer.`}
        canonicalPath={`https://www.${DOMAIN}/enterprise-tos`}
      />
      <PublicNav />
      <StyledContainer className="content-page" mt={STANDARD_NAV_HEIGHT}>
        <Box pt={4} className="public-content">
          <h1>{SITE_NAME} Enterprise Terms of Service</h1>
          <Flex>
            <div>Last updated: February 1, 2025</div>
            <div>・</div>
            <ExportButton
              contentRef={contentRef}
              filename="exec-enterprise-tos.docx"
              title="Exec Enterprise Terms of Service"
            />
          </Flex>
          <hr />
          <div ref={contentRef}>
            <DocumentContent />
          </div>
        </Box>
      </StyledContainer>
      <PublicFooter />
    </>
  );
}
